// library imports
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import {
  Button,
  Tooltip,
  Modal,
  Divider,
  Flex,
  Spin,
  Select,
  Popconfirm,
} from "antd";
import { useEffect, useState, useContext } from "react";
// component imports
import RevenueForm from "./RevenueForm";
import DataTable from "../../../components/common/DataTable";
// context imports
import { IndexContext } from "../../../context/IndexContext";
// hook imports
import useFetch from "../../../hooks/useFetch";

export default function RevenueView() {
  // index context
  const {
    siteSelected,
    setSiteSelected,
    siteList,
    getSiteSelectionLoading,
    messageApi,
  } = useContext(IndexContext);

  // fetch wrapper
  const [fetchWrapper] = useFetch();

  // revenue data to be displayed in table
  const [revenueDetails, setRevenueDetails] = useState([]);

  // revenue table loading state
  const [revenueTableLoading, setRevenueTableLoading] = useState(false);

  // add modal opening state
  const [isAddRevenueModalOpen, setIsAddRevenueModalOpen] = useState(false);

  // edit modal opening state
  const [isEditRevenueModalOpen, setIsEditRevenueModalOpen] = useState(false);

  // edit form initial values
  const [formInitialValues, setFormInitialValues] = useState({});

  // year list to be disabled for date picker in revenue form
  const [disabledYears, setDisabledYears] = useState([]);

  const revenueTableColumns = [
    {
      title: "Year",
      dataIndex: "Year",
      key: "Year",
    },
    {
      title: "Revenue",
      dataIndex: "Revenue",
      key: "Revenue",
    },
    {
      title: "",
      dataIndex: "Action",
      key: "Action",
      width: "11%",
      render: (_, record) => (
        <Flex justify="flex-start" align="center" gap={"5px"} wrap={true}>
          <Tooltip title="Edit">
            <Button
              type="text"
              icon={<EditOutlined style={{ color: "blue" }} />}
              onClick={() => {
                getRevenueFormData(record);
                setIsEditRevenueModalOpen(true);
              }}
            />
          </Tooltip>

          <Tooltip title="Delete">
            <Popconfirm
              title="Delete Revenue"
              description={"Are you sure to delete this revenue?"}
              onConfirm={() => {
                deleteRevenue(record);
              }}
              okText="Yes"
              cancelText="No"
            >
              <Button
                type="text"
                icon={<DeleteOutlined style={{ color: "red" }} />}
              />
            </Popconfirm>
          </Tooltip>
        </Flex>
      ),
    },
  ];

  /* API to get revenue form data to initialise form when form is in 'edit' mode */
  function getRevenueFormData(row_record) {
    fetchWrapper({
      method: "GET",
      endpoint_url: "revenue_management/getRevenueFormData",
      set_content_type_header: false,
      params: {
        revenue_id: row_record.ID,
      },
      onSuccess: (fetch_output) => {
        setFormInitialValues(fetch_output.data);
      },
    });
  }

  /* API to get revenue table data */
  function getRevenueDetails() {
    setRevenueTableLoading(true);
    fetchWrapper({
      method: "GET",
      endpoint_url: "revenue_management/getRevenueDetails",
      set_content_type_header: false,
      params: {
        site_id: siteSelected,
      },
      onSuccess: (fetch_output) => {
        setRevenueTableLoading(false);
        setRevenueDetails(fetch_output.data);
      },
    });
  }

  /** API to delete revenue */
  function deleteRevenue(row_record) {
    setRevenueTableLoading(true);
    fetchWrapper({
      endpoint_url: "revenue_management/deleteRevenue",
      body: {
        revenue_id: row_record.ID,
      },
      onSuccess: (fetch_output) => {
        setRevenueTableLoading(false);
        if (fetch_output.data === true) {
          messageApi.open({
            type: "success",
            content: fetch_output.message,
          });
          getRevenueDetails();
          getYearList();
        } else {
          messageApi.open({
            type: "error",
            content: fetch_output.message,
          });
        }
      },
    });
  }

  /* API to get year list of the selected site which the revenue has been input */
  function getYearList() {
    fetchWrapper({
      method: "GET",
      endpoint_url: "revenue_management/getYearList",
      set_content_type_header: false,
      params: {
        site_id: siteSelected,
      },
      onSuccess: (fetch_output) => {
        setDisabledYears(fetch_output.data);
      },
    });
  }

  useEffect(() => {
    if (siteSelected) {
      getRevenueDetails();
      getYearList();
    }
  }, [siteSelected]);

  return (
    <>
      <Flex align="center">
        <p className="mr-2">Site:</p>
        <Select
          style={{ width: "100%", textAlign: "left" }}
          loading={getSiteSelectionLoading}
          showSearch
          value={siteSelected}
          options={siteList}
          optionFilterProp="label"
          labelRender={(value) => <>{value.label}</>}
          onChange={(value) => {
            setSiteSelected(value);
          }}
        />
      </Flex>

      <Flex>
        <Button
          icon={<PlusOutlined />}
          className="mt-3 mb-5"
          onClick={() => setIsAddRevenueModalOpen(true)}
          disabled={revenueTableLoading}
        >
          Revenue
        </Button>
      </Flex>

      {revenueTableLoading === true ? (
        <Flex justify="center" align="center" style={{ height: "50vh" }}>
          <Spin />
        </Flex>
      ) : (
        <DataTable
          table_column={revenueTableColumns}
          table_data={revenueDetails}
          scroll_width={1500}
        />
      )}

      <Modal
        title="Add Revenue"
        open={isAddRevenueModalOpen}
        footer={null}
        closable={false}
        maskClosable={false}
      >
        <Divider className="mb-4 mt-0"></Divider>
        <RevenueForm
          mode="add"
          setIsModalOpen={setIsAddRevenueModalOpen}
          refreshTable={getRevenueDetails}
          refreshYearList={getYearList}
          siteSelected={siteSelected}
          disabledYears={disabledYears}
        />
      </Modal>

      <Modal
        title="Edit Revenue"
        open={isEditRevenueModalOpen}
        footer={null}
        closable={false}
        maskClosable={false}
      >
        <Divider className="mb-4 mt-0"></Divider>
        <RevenueForm
          mode="edit"
          setIsModalOpen={setIsEditRevenueModalOpen}
          refreshTable={getRevenueDetails}
          initialValues={formInitialValues}
          disabledYears={disabledYears}
        />
      </Modal>
    </>
  );
}
